import * as pr from 'preact'
import * as a from '@mitranim/js/all.mjs'
import * as p from '@mitranim/js/prax.mjs'

/*
Same as `pr.createElement`, but with additional validation and transformations.
*/
export function E(type, props, ...children) {
  validateType(type)
  props = p.deref(props)
  if (a.isStr(type)) fixProps(props)
  fixChildren(children)
  return pr.h(type, props, ...children)
}

function validateType(val) {
  if (!(a.isStr(val) || a.isFun(val))) {
    throw Error(`JSX element type must be string or function, got ${val}`)
  }
}

function fixChildren(val) {
  // Passing a function as the only child is a semi-rare pattern
  // required by some of the libraries we use.
  if (val.length === 1 && a.isFun(val[0])) return

  val.forEach(fixChildAt)
}

function fixChildAt(val, key, children) {
  if (isRenderable(val)) children[key] = val = val.render()

  if (pr.isValidElement(val)) {
    if (a.isFun(val.type)) return

    if (a.isStr(val.type)) {
      fixProps(val.props)
      return
    }

    throw Error(`missing virtual element type on ${a.show(val)}`)
  }

  if (a.isArr(val)) {
    fixChildren(val)
    return
  }

  if (a.isPrim(val)) return

  /*
  Preact is too dumb for this, and considers all non-array objects to be
  "virtual elements", producing either nothing at all, or weird shit like
  `[object Object]`, or weird exceptions.

  Unfortunately we can't do this for props, because they're often used to
  pass abstract data to descendants.
  */
  if (a.isScalar(val)) {
    children[key] = String(val)
    return
  }

  throw Error(`child ${a.show(val)} is not stringable, which usually indicates a bug`)
}

/*
Adapted from Prax: when rendering plain elements, avoid stringifying props
whose values are objects without a custom `.toString()`. Preact is too dumb
for this, and will happily render weird shit like `[object Object]`, which
is always a bug.

Must be called only for plain elements (type = string).

TODO: instead of skipping these props, throw exceptions, like in Prax.
Skipping is more convenient for now.
*/
function fixProps(props) {
  if (!props) return

  for (const key in props) {
    const val = props[key]

    if (key === `children`) {
      fixChildAt(val, key, props)
      continue
    }

    if (a.isPrim(val) || a.isFun(val) || allowedComplexProps.has(key)) {
      continue
    }

    if (!a.isScalar(val)) a.priv(props, key, val)
  }
}

const allowedComplexProps = new Set([`style`, `dangerouslySetInnerHTML`, `ref`])

function isRenderable(val) {return a.hasMeth(val, `render`)}
